"use client"

import { useState, useRef, useEffect } from "react"
import { User, LogOut, Settings, Menu, X } from "lucide-react"
import Link from "next/link"
import { browserAgent } from "@/lib/browser-agent"
import { logoutAction } from "@/app/(main)/(auth)/logout/actions"
import { usePathname } from "next/navigation"
import type { Session } from "next-auth"

interface CPHeaderInteractiveProps {
    session: Session | null
    position: 'desktop' | 'mobile-menu' | 'mobile-user'
}

export function CPHeaderInteractive({ session, position }: CPHeaderInteractiveProps) {
    const pathname = usePathname()

    const activePage = (() => {
        if (pathname?.startsWith('/cp/account')) return 'account'
        if (pathname?.startsWith('/cp/promo')) return 'promo'
        if (pathname?.startsWith('/pricing')) return 'payments'
        if (pathname?.startsWith('/cp/processing') || pathname === '/cp') return 'processing'
        return 'processing'
    })()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [headerHeight, setHeaderHeight] = useState(72)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const mobileMenuRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const handleSignOut = async () => {
        browserAgent.reset()
        await logoutAction()
    }

    useEffect(() => {
        const updateHeaderHeight = () => {
            const header = buttonRef.current?.closest('header')
            if (header) {
                setHeaderHeight(header.offsetHeight)
            }
        }

        updateHeaderHeight()
        window.addEventListener('resize', updateHeaderHeight)
        return () => window.removeEventListener('resize', updateHeaderHeight)
    }, [])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setIsMobileMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const getMobileNavItemClass = (page: string) => {
        const baseClass = "onest-nav transition-all duration-300 px-6 sm:px-8 py-3 block"
        return activePage === page
            ? `${baseClass} text-black bg-gray-100 font-semibold`
            : `${baseClass} text-gray-700 hover:bg-gray-50`
    }

    if (position === 'desktop') {
        return session ? (
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors duration-200"
                >
                    <User className="w-5 h-5 text-gray-600" />
                </button>

                {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        {/* User Info Section */}
                        <div className="px-4 py-3 border-b border-gray-100">
                            <div className="text-sm font-medium text-gray-900 truncate">
                                {session.user.name || 'Пользователь'}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                                {session.user.email}
                            </div>
                        </div>

                        {/* Menu Items */}
                        <Link
                            href="/cp/account"
                            className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                            onClick={() => setIsDropdownOpen(false)}
                        >
                            <Settings className="w-4 h-4" />
                            Настройки
                        </Link>
                        <button
                            onClick={() => {
                                setIsDropdownOpen(false)
                                handleSignOut()
                            }}
                            className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors duration-200 w-full text-left"
                        >
                            <LogOut className="w-4 h-4" />
                            Выход
                        </button>
                    </div>
                )}
            </div>
        ) : null
    }

    if (position === 'mobile-menu') {
        return (
            <>
                <button
                    ref={buttonRef}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="flex items-center p-0 hover:opacity-70 cursor-pointer transition-opacity duration-200"
                >
                    {isMobileMenuOpen ? (
                        <X className="w-6 h-6 text-gray-600" />
                    ) : (
                        <Menu className="w-6 h-6 text-gray-600" />
                    )}
                </button>

                {isMobileMenuOpen && (
                    <div
                        ref={mobileMenuRef}
                        className="fixed inset-x-0 bg-white border-b border-gray-200 shadow-lg z-[9999]"
                        style={{ top: `${headerHeight - 1}px` }}
                    >
                        <nav className="py-2 container mx-auto max-w-7xl">
                            <Link
                                href="/cp/processing"
                                className={getMobileNavItemClass('processing')}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Обработать
                            </Link>
                            <Link
                                href="/pricing"
                                className={getMobileNavItemClass('payments')}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Пополнить баланс
                            </Link>
                            <Link
                                href="/cp/promo"
                                className={getMobileNavItemClass('promo')}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Промокод
                            </Link>

                            {session && (
                                <>
                                    <div className="border-t border-gray-200 my-2"></div>

                                    {/* User Info Section */}
                                    <div className="px-6 sm:px-8 py-3 border-b border-gray-100">
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                            {session.user.name || 'Пользователь'}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate">
                                            {session.user.email}
                                        </div>
                                    </div>

                                    <Link
                                        href="/cp/account"
                                        className="flex items-center gap-3 px-6 sm:px-8 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors duration-200 onest-nav"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <Settings className="w-4 h-4" />
                                        Настройки
                                    </Link>
                                    <button
                                        onClick={() => {
                                            setIsMobileMenuOpen(false)
                                            handleSignOut()
                                        }}
                                        className="flex items-center gap-3 px-6 sm:px-8 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors duration-200 w-full text-left onest-nav"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Выход
                                    </button>
                                </>
                            )}
                        </nav>
                    </div>
                )}
            </>
        )
    }

    if (position === 'mobile-user') {
        return session ? (
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors duration-200"
            >
                <User className="w-5 h-5 text-gray-600" />
            </button>
        ) : null
    }

    return null
}

